import axios from 'axios';
import SecureLogger from './secureLogger.js';
import API_CONFIG from '../config/api.js';
import { getAuthorizedCompanyContext } from './companyContext.js';
import { showCompanySuspensionNotice } from './companySuspensionNotice.js';
import {
  installAxiosGlobalLoading,
  runWithGlobalLoading,
} from './globalLoading.js';
import {
  emitSubscriptionAccessUpdate,
  isSubscriptionRestrictionCode,
} from './subscriptionAccessEvents.js';

const isApplicationApiRequest = (input) => {
  if (typeof window === 'undefined') return false;

  try {
    const rawUrl =
      typeof Request !== 'undefined' && input instanceof Request
        ? input.url
        : String(input);
    const requestUrl = new URL(rawUrl, window.location.origin);
    const configuredApiUrl = new URL(
      API_CONFIG.BASE_URL || window.location.origin,
      window.location.origin
    );

    return (
      requestUrl.pathname.startsWith('/api/') &&
      (requestUrl.origin === window.location.origin ||
        requestUrl.origin === configuredApiUrl.origin)
    );
  } catch {
    return false;
  }
};

const mergeFetchHeaders = (input, init = {}) => {
  const requestHeaders =
    typeof Request !== 'undefined' && input instanceof Request
      ? input.headers
      : undefined;
  const headers = new Headers(requestHeaders);

  if (init.headers) {
    new Headers(init.headers).forEach((value, key) => {
      headers.set(key, value);
    });
  }

  return headers;
};

const addAuthenticatedCompanyContext = (config) => {
  const token = localStorage.getItem('authToken');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    const companyContextId = getAuthorizedCompanyContext();
    if (companyContextId) {
      config.headers['X-Company-Id'] = String(companyContextId);
    }
  }

  return config;
};

const inspectSubscriptionAccessResponse = async (response) => {
  if (response?.status !== 403 || typeof response.clone !== 'function') {
    return response;
  }

  try {
    const payload = await response.clone().json();
    if (isSubscriptionRestrictionCode(payload?.code)) {
      emitSubscriptionAccessUpdate(payload);
    } else if (payload?.code === 'COMPANY_SUSPENDED') {
      showCompanySuspensionNotice(payload);
    }
  } catch {
    // Une réponse non JSON reste gérée par le composant appelant.
  }
  return response;
};

/**
 * Configuration d'un intercepteur Axios pour gérer automatiquement
 * les erreurs d'authentification, notamment l'expiration des essais
 */
export const setupApiInterceptor = (authContextRef) => {
  const cleanupAxiosLoading = installAxiosGlobalLoading(axios);

  // Certains modules historiques utilisent fetch directement au lieu d'Axios.
  // Leur transmettre le meme contexte evite qu'une action realisee dans une
  // entreprise secondaire soit silencieusement executee dans la principale.
  const originalFetch =
    typeof window !== 'undefined' && typeof window.fetch === 'function'
      ? window.fetch
      : null;
  let companyScopedFetch = null;

  if (originalFetch && typeof Headers !== 'undefined') {
    companyScopedFetch = (input, init = {}) => {
      const {
        skipGlobalLoading,
        globalLoading,
        ...fetchInit
      } = init || {};
      const headers = mergeFetchHeaders(input, fetchInit);
      const token = localStorage.getItem('authToken');
      const authorization = headers.get('Authorization')?.trim();
      const isStaffRequest =
        token && authorization === `Bearer ${token}`;

      if (
        isStaffRequest &&
        isApplicationApiRequest(input) &&
        !headers.has('X-Company-Id')
      ) {
        const companyContextId = getAuthorizedCompanyContext();
        if (companyContextId) {
          headers.set('X-Company-Id', String(companyContextId));
        }
      }

      const executeFetch = () => {
        if (typeof Request !== 'undefined' && input instanceof Request) {
          return originalFetch.call(
            window,
            new Request(input, { ...fetchInit, headers })
          );
        }

        return originalFetch.call(window, input, { ...fetchInit, headers });
      };

      if (!isApplicationApiRequest(input)) {
        return executeFetch();
      }

      return Promise.resolve(
        runWithGlobalLoading(executeFetch, {
          skipGlobalLoading,
          globalLoading,
        })
      ).then(inspectSubscriptionAccessResponse);
    };

    window.fetch = companyScopedFetch;
  }
  
  // Intercepteur de réponse pour gérer les erreurs
  const responseInterceptor = axios.interceptors.response.use(
    (response) => {
      // Si la réponse est OK, la laisser passer
      return response;
    },
    (error) => {
      SecureLogger.debug('[API Interceptor] Error detected in response');
      
      if (error.response?.status === 403) {
        const errorData = error.response.data;

        if (isSubscriptionRestrictionCode(errorData?.code)) {
          emitSubscriptionAccessUpdate(errorData);
        } else if (errorData?.code === 'COMPANY_SUSPENDED') {
          showCompanySuspensionNotice(errorData);
        }
      }
      
      // Vérifier si c'est une erreur 401 avec code ADMIN_SESSION_EXPIRED
      if (error.response?.status === 401) {
        const errorData = error.response.data;
        
        if (errorData?.code === 'ADMIN_SESSION_EXPIRED') {
          SecureLogger.info('[API Interceptor] Admin session expired detected, forcing logout');
          
          // Force logout et redirection
          if (authContextRef?.current?.logout) {
            authContextRef.current.logout();
            setTimeout(() => {
              window.location.href = '/login?reason=admin_session_expired';
            }, 100);
          }
        }
      }
      
      // Pour toutes les autres erreurs, rejeter normalement
      return Promise.reject(error);
    }
  );

  // Intercepteur de requête pour ajouter automatiquement le token
  const requestInterceptor = axios.interceptors.request.use(
    addAuthenticatedCompanyContext,
    (error) => {
      return Promise.reject(error);
    }
  );

  // Retourner une fonction de nettoyage pour supprimer les intercepteurs
  return () => {
    cleanupAxiosLoading();
    axios.interceptors.request.eject(requestInterceptor);
    axios.interceptors.response.eject(responseInterceptor);
    if (
      originalFetch &&
      companyScopedFetch &&
      window.fetch === companyScopedFetch
    ) {
      window.fetch = originalFetch;
    }
  };
};

// Instance Axios pré-configurée (optionnel)
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 10000,
});

installAxiosGlobalLoading(apiClient);

apiClient.interceptors.request.use(
  addAuthenticatedCompanyContext,
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      showCompanySuspensionNotice(error.response.data);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
